// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
export type ApprovalCrossCheck = {
  state?: string;
  message?: string;
  timesheetValue?: number | null;
  payslipValue?: number | null;
  difference?: number | null;
};

export type ApprovalResolutionCheckKey =
  | "workload"
  | "overtime"
  | "vacation"
  | "doctor"
  | "familyCare"
  | "illness"
  | "publicHoliday"
  | "paidLeave"
  | "remainingVacation";

export type ApprovalIssueReviewTarget =
  | { kind: "resolution"; checkKey: ApprovalResolutionCheckKey }
  | { kind: "result"; resultId: number };

type ApprovalResultInput = {
  id: number;
  userId: number;
  employeeName: string;
  payrollEmployeeCode: string;
  hoursPerDay: number;
  statusColor: string;
  crossChecks?: Record<string, ApprovalCrossCheck | undefined>;
  warnings?: string[];
  errors?: string[];
};

export type ApprovalBlockingIssue = ApprovalCrossCheck & {
  id: string;
  checkKey: string;
  label: string;
  message: string;
  reviewTarget: ApprovalIssueReviewTarget;
};

export type ApprovalBlockingEmployee = {
  resultId: number;
  userId: number;
  employeeName: string;
  payrollEmployeeCode: string;
  hoursPerDay: number;
  issues: ApprovalBlockingIssue[];
};

export const shouldShowApprovalBlockingLog = ({
  activeStageId,
  blockingCheckCount,
  calculationInputsReady,
}: {
  activeStageId: string;
  blockingCheckCount: number;
  calculationInputsReady: boolean;
}) =>
  activeStageId === "reconcile" &&
  blockingCheckCount > 0 &&
  calculationInputsReady;

const CHECK_LABELS: Record<string, string> = {
  workload: "Payslip worked coverage",
  overtime: "Overtime",
  remainingVacation: "Vacation remaining",
  vacation: "Vacation taken",
  doctor: "Doctor leave",
  familyCare: "Family care",
  illness: "Illness",
  publicHoliday: "Public holidays",
  paidLeave: "Paid leave",
};

const RESOLUTION_CHECK_KEYS = new Set<ApprovalResolutionCheckKey>([
  "workload",
  "overtime",
  "vacation",
  "doctor",
  "familyCare",
  "illness",
  "publicHoliday",
  "paidLeave",
  "remainingVacation",
]);

const NON_BLOCKING_CHECK_KEYS = new Set(["overtime", "remainingVacation"]);

const isResolutionCheckKey = (
  key: string,
): key is ApprovalResolutionCheckKey =>
  RESOLUTION_CHECK_KEYS.has(key as ApprovalResolutionCheckKey);

const humanizeCheckKey = (key: string) => CHECK_LABELS[key]
  ?? key.replace(/([a-z])([A-Z])/g, "$1 $2").replaceAll("_", " ").replace(/^./, (letter) => letter.toLocaleUpperCase());

const defaultCheckMessage = (check: ApprovalCrossCheck) => {
  if (check.timesheetValue === null && check.payslipValue === null) return "Both comparison values are missing.";
  if (check.timesheetValue === null) return "The timesheet value is missing.";
  if (check.payslipValue === null) return "The payslip value is missing.";
  return "The timesheet and payslip values do not match.";
};

const uniqueIssues = (issues: ApprovalBlockingIssue[]) => {
  const seen = new Set<string>();
  return issues.filter((issue) => {
    const reviewSignature =
      issue.reviewTarget.kind === "resolution"
        ? issue.reviewTarget.checkKey
        : `result-${issue.reviewTarget.resultId}`;
    const valueSignature =
      issue.reviewTarget.kind === "result"
        ? ""
        : `${issue.timesheetValue ?? ""}\u0000${issue.payslipValue ?? ""}\u0000${issue.difference ?? ""}`;
    const signature = `${issue.label}\u0000${issue.message}\u0000${reviewSignature}\u0000${valueSignature}`;
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
};

export const buildApprovalBlockingLog = (results: ApprovalResultInput[]): ApprovalBlockingEmployee[] =>
  results.flatMap((result) => {
    const issues: ApprovalBlockingIssue[] = [];
    let ignoredNonBlockingDifference = false;

    (result.errors ?? []).filter(Boolean).forEach((message, index) => {
      issues.push({
        id: `error-${index}`,
        checkKey: "calculation",
        label: "Calculation error",
        message,
        reviewTarget: { kind: "result", resultId: result.id },
      });
    });

    Object.entries(result.crossChecks ?? {}).forEach(([checkKey, check]) => {
      if (!check || check.state !== "red") return;
      if (NON_BLOCKING_CHECK_KEYS.has(checkKey)) {
        ignoredNonBlockingDifference = true;
        return;
      }
      issues.push({
        ...check,
        id: `check-${checkKey}`,
        checkKey,
        label: humanizeCheckKey(checkKey),
        message: check.message?.trim() || defaultCheckMessage(check),
        reviewTarget: isResolutionCheckKey(checkKey)
          ? { kind: "resolution", checkKey }
          : { kind: "result", resultId: result.id },
      });
    });

    if (result.statusColor !== "red" && issues.length === 0) return [];
    if (issues.length === 0 && ignoredNonBlockingDifference) return [];

    if (issues.length === 0) {
      const warnings = (result.warnings ?? []).filter(Boolean);
      if (warnings.length) {
        warnings.forEach((message, index) => {
          issues.push({
            id: `warning-${index}`,
            checkKey: "calculation",
            label: "Blocking calculation warning",
            message,
            reviewTarget: { kind: "result", resultId: result.id },
          });
        });
      } else {
        issues.push({
          id: "blocking-result",
          checkKey: "calculation",
          label: "Blocking calculation result",
          message: "This employee has a red calculation result, but the calculation did not return a more specific explanation.",
          reviewTarget: { kind: "result", resultId: result.id },
        });
      }
    }

    return [{
      resultId: result.id,
      userId: result.userId,
      employeeName: result.employeeName,
      payrollEmployeeCode: result.payrollEmployeeCode,
      hoursPerDay: result.hoursPerDay,
      issues: uniqueIssues(issues),
    }];
  });
